
import { Client, SocialProfiles } from "./types";
import { EnrichedData, MappableClientField } from "./enrichment-dialog/enrichmentTypes";

// Type guard for unknown values that might be objects
function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// Type guard for string arrays
function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(item => typeof item === 'string');
}

// Type guard for valid social profiles
function isSocialProfiles(value: unknown): value is Record<string, string> {
  return isObject(value) && Object.values(value).every(v => typeof v === 'string');
}

// Update mappableFields with proper typing
export const mappableFields: Array<MappableClientField<keyof Client, unknown>> = [
  { key: 'name', enrichedKey: ['name', 'company_name'], label: 'Company Name' },
  { key: 'description', enrichedKey: ['description', 'company_description', 'overview'], label: 'Description' },
  { key: 'industry', enrichedKey: 'industry', label: 'Industry' },
  { key: 'website', enrichedKey: ['website', 'website_url', 'domain'], label: 'Website' },
  { 
    key: 'employee_count', 
    enrichedKey: ['employee_count', 'employees', 'employee_range'], 
    label: 'Employee Count',
    transform: (value: unknown): string | null => {
      if (value === null || value === undefined) return null;
      return String(value);
    }
  },
  { 
    key: 'revenue_range', 
    enrichedKey: ['revenue_range', 'annual_revenue', 'revenue'], 
    label: 'Revenue Range',
    transform: (value: unknown): string | null => {
      if (value === null || value === undefined) return null;
      return String(value);
    }
  },
  { 
    key: 'founded_year', 
    enrichedKey: 'founded_year', 
    label: 'Founded Year',
    transform: (value: unknown): number | null => {
      if (value === null || value === undefined) return null;
      const parsed = parseInt(String(value), 10);
      return isNaN(parsed) ? null : parsed;
    }
  },
  { key: 'headquarters_location', enrichedKey: ['headquarters_location', 'hq_location', 'address', 'location_string'], label: 'Headquarters' },
  { 
    key: 'social_profiles', 
    enrichedKey: 'social_profiles', 
    label: 'Social Profiles',
    transform: (value: unknown): SocialProfiles | null => {
      if (!isSocialProfiles(value)) return null;
      
      const profiles: Partial<SocialProfiles> = {};
      if (value.linkedin && typeof value.linkedin === 'string') profiles.linkedin = value.linkedin;
      if (value.twitter && typeof value.twitter === 'string') profiles.twitter = value.twitter;
      if (value.facebook && typeof value.facebook === 'string') profiles.facebook = value.facebook;
      if (value.github && typeof value.github === 'string') profiles.github = value.github;
      
      return Object.keys(profiles).length > 0 ? profiles as SocialProfiles : null;
    }
  },
  { 
    key: 'technologies_used', 
    enrichedKey: ['technologies_used', 'tech_stack', 'technologies'], 
    label: 'Technologies Used',
    transform: (value: unknown): string[] | null => {
      if (isStringArray(value)) {
        return value.map(String).filter(tech => tech.trim() !== "");
      }
      if (typeof value === 'string') {
        return value.split(',').map(s => s.trim()).filter(tech => tech.trim() !== "");
      }
      return null;
    }
  },
];

export const getEnrichedValue = (data: EnrichedData | null, enrichedKey: string | string[]): unknown => {
  if (!data) return undefined;
  if (Array.isArray(enrichedKey)) {
    for (const key of enrichedKey) {
      const val = data[key];
      if (val !== undefined && val !== null && String(val).trim() !== "") {
        return val;
      }
    }
    return undefined;
  }
  const val = data[enrichedKey];
  if (val !== undefined && val !== null && String(val).trim() !== "") {
    return val;
  }
  return undefined;
};

export const compareClientDataWithEnriched = (
  client: Client,
  enrichedData: EnrichedData | null,
  fieldsToMap: Array<MappableClientField<keyof Client, unknown>>
): Set<keyof Client> => {
  const changedFields = new Set<keyof Client>();
  if (!enrichedData) return changedFields;

  fieldsToMap.forEach(field => {
    const currentValue = client[field.key];
    const enrichedValueRaw = getEnrichedValue(enrichedData, field.enrichedKey);
    
    let transformedEnrichedValue: unknown = enrichedValueRaw;
    if (field.transform && enrichedValueRaw !== undefined && enrichedValueRaw !== null) {
      try {
        transformedEnrichedValue = field.transform(enrichedValueRaw);
      } catch (e) {
        console.warn(`Transform failed for field ${String(field.key)} with value:`, enrichedValueRaw, e);
        transformedEnrichedValue = undefined;
      }
    }

    let isDifferent = false;
    if (transformedEnrichedValue !== undefined && transformedEnrichedValue !== null) {
      if (field.key === 'social_profiles') {
        const currentSocial = (currentValue as SocialProfiles) || {};
        const newSocial = (transformedEnrichedValue as SocialProfiles) || {};
        isDifferent = JSON.stringify(currentSocial) !== JSON.stringify(newSocial) && Object.keys(newSocial).length > 0;
      } else if (field.key === 'technologies_used') {
        const currentTech = (currentValue as string[] || []).map(s=>s.toLowerCase()).sort();
        const newTech = (transformedEnrichedValue as string[] || []).map(s=>s.toLowerCase()).sort();
        isDifferent = JSON.stringify(currentTech) !== JSON.stringify(newTech) && newTech.length > 0;
      } else if (field.key === 'founded_year') {
        isDifferent = Number(transformedEnrichedValue) !== Number(currentValue);
      } else {
        const currentString = currentValue === null || currentValue === undefined ? "" : String(currentValue);
        const newString = String(transformedEnrichedValue);
        isDifferent = newString.trim() !== "" && newString !== currentString;
      }
    }
    
    if (isDifferent) {
      changedFields.add(field.key);
    }
  });

  return changedFields;
};
